/*
 * FILE: unified_ui/src/stores/chat.ts
 * PURPOSE: Pinia store for the Chat tab transcript. Loads history from Core via
 *          NATS proxy, sends messages to the chat agent, and handles action chips.
 *          Agent threads: tracks currentThreadId, "Новый чат" (cmd.core.chat.new),
 *          and renders thread-boundary dividers (local pseudo-messages).
 * OWNS: transcript state, sendMessage, loadHistory, loadMoreHistory, newChat,
 *       thread divider building (withThreadDividers), action chip routing,
 *       transient agent-run progress (activity + toolDetails accumulation).
 * EXPORTS: useChatStore, withThreadDividers, threadDividerLabel, ChatMessage, ChatRole.
 */
import { defineStore } from 'pinia'
import { call } from '@/lib/botSuiteClient'
import { authHeaders, getSessionToken } from '@/lib/sessionToken'
import { withThreadDividers, threadDividerLabel } from '@/lib/threadDividers'
import { trackEvent, trackUserFacingError } from '@/lib/trackEvent'
import {
  CMD,
  type MessageItem,
  type MessagesListRequest,
  type MessagesListResponse,
  type AgentMessageRequest,
  type AgentMessageResponse,
  type AgentCancelResponse,
  type ChatAction,
  type ChatFile,
  type ChatNewResponse,
} from '@/lib/commands'

export type { ChatAction, ChatFile }
export { withThreadDividers, threadDividerLabel }

export type ChatRole = 'user' | 'assistant' | 'system' | 'agent' | 'divider'

/** Semantic buckets emitted by botCore (never raw tool names). */
export type ActivityStage = 'research' | 'catalog' | 'workspace' | 'comms' | 'answer' | 'other'

/** The one evolving line shown while a run is active. */
export type AgentActivity = {
  stage: ActivityStage
  intent: string
  /** Epoch ms when this intent started — drives the elapsed hint. */
  since: number
  failed?: boolean
}

/** One closed/ongoing tool step for the expandable detail list. */
export type ActivityDetail = {
  label: string
  durationMs?: number
  failed?: boolean
  running?: boolean
}

/** Shape published by botCore and relayed by webBridge (SSE + polling). */
export type AgentProgressPayload = {
  phase: string
  stage?: ActivityStage
  intent?: string
  failed?: boolean
  toolName?: string
  toolLabel?: string
  durationMs?: number
  delta?: string
}

export type ChatMessage = {
  id: string
  role: ChatRole
  text: string
  ts: string
  actions?: ChatAction[]
  /** Uploaded files (user) or downloadable artifacts (agent). */
  files?: ChatFile[]
  /** True only for messages pushed live from the current send() response.
   *  History-mapped messages stay unmarked. Live messages carrying a
   *  `new_order` action trigger the auto-redirect countdown. */
  live?: boolean
  /** Persisted thread id (null = legacy pre-thread conversation). Divider
   *  pseudo-messages carry the thread they open — they are NEVER persisted
   *  or sent to the agent. */
  threadId?: number | null
  /** Thread kind resolved from server thread metadata. 'static' = adapter
   *  visibility copies (the user's bot conversation from Telegram/Max/media —
   *  NOT part of this dialog, inserted only for continuity). The message
   *  surface badges those rows with «Из переписки с ботом». Agent/legacy
   *  messages carry no kind (undefined). */
  threadKind?: 'agent' | 'static' | null
  /** Tool steps of the in-flight run (synthetic pending row only). */
  details?: ActivityDetail[]
  /** Epoch ms when the current activity line started (synthetic row only). */
  activitySince?: number
  /** True while the pending row carries streamed answer text. */
  streaming?: boolean
}

const PAGE_SIZE = 40

const FALLBACK_NO_AGENT_TEXT =
  'Агент временно недоступен. Попробуйте позже или воспользуйтесь кнопками ниже.'

const FALLBACK_NO_AGENT_ACTIONS: ChatAction[] = [
  { label: 'Мои КП', kind: 'open_orders' },
  { label: 'Поддержка', kind: 'support' },
]

function greetingMessage(): ChatMessage {
  return {
    id: 'm_greeting',
    role: 'agent',
    text: 'Привет! Я помогу подобрать оборудование или ПО. Что ищете?',
    ts: new Date().toISOString(),
  }
}

function mapMessage(
  m: MessageItem,
  kinds?: Map<number, 'agent' | 'static'>,
): ChatMessage {
  let actions: ChatAction[] | undefined
  let files: ChatFile[] | undefined
  if (m.meta) {
    try {
      const parsed = JSON.parse(m.meta)
      if (Array.isArray(parsed.actions)) actions = parsed.actions
      if (Array.isArray(parsed.files)) files = parsed.files
    } catch { /* meta is not valid JSON — ignore */ }
  }
  return {
    id: `mh_${m.id}`,
    role: m.role as ChatRole,
    text: m.text,
    ts: m.timestamp,
    actions,
    files,
    threadId: m.threadId,
    threadKind: m.threadId != null ? (kinds?.get(m.threadId) ?? null) : null,
  }
}

type State = {
  messages: ChatMessage[]
  draft: string
  sending: boolean
  loadedHistory: boolean
  loadingHistory: boolean
  loadingMore: boolean
  hasMore: boolean
  lastPollTimestamp: string
  pollInterval: ReturnType<typeof setInterval> | null
  /** In-app notification from evt.chat.send (e.g. search completed). */
  notification: { id: string; text: string } | null
  /** Real-time agent progress status for the status bar. */
  agentStatus: { phase: string; label: string } | null
  agentStatusPollId: ReturnType<typeof setInterval> | null
  /** The single evolving activity line for the current run (transient). */
  activity: AgentActivity | null
  /** Tool-level detail behind the activity line, in start order (transient). */
  toolDetails: ActivityDetail[]
  /** True while the SSE progress stream is connected; polling is a fallback. */
  statusStreamLive: boolean
  /** Monotonic run counter: incremented on every send() and every
   *  stopStatusPolling(). An in-flight pollAgentStatus() fetch captured an
   *  epoch before resolving; if its epoch no longer matches, its result is
   *  stale (the run it belonged to already finished) and must be discarded. */
  runEpoch: number
  /** Cancel function of the currently pending auto-redirect countdown
   *  (registered by the message surface for a live `new_order` message). */
  pendingRedirectCancel: (() => void) | null
  /** Active agent thread id (null = legacy pre-thread conversation). Set by
   *  history load and by newChat(); sent with every agent message. */
  currentThreadId: number | null
  /**
   * Host-page prefill (e.g. WP product info from `[bot_suite_v2 prefill="auto"]`).
   * Set on mount from `?prefill=...` on the iframe URL. Persists for the
   * session — appended to the system prompt on every turn so the agent has
   * the host context from turn 0 without the user having to type it.
   */
  hostContext: string | null
}

export const useChatStore = defineStore('chat', {
  state: (): State => ({
    messages: [],
    draft: '',
    sending: false,
    loadedHistory: false,
    loadingHistory: false,
    loadingMore: false,
    hasMore: false,
    lastPollTimestamp: new Date().toISOString(),
    pollInterval: null,
    notification: null,
    agentStatus: null,
    agentStatusPollId: null,
    activity: null,
    toolDetails: [],
    statusStreamLive: false,
    runEpoch: 0,
    pendingRedirectCancel: null,
    currentThreadId: null,
    hostContext: null,
  }),
  getters: {
    lastMessage: (s) => s.messages[s.messages.length - 1] ?? null,
  },
  actions: {
    setDraft(value: string) {
      this.draft = value
    },

    /**
     * Set the host-page prefill (e.g. WP product info from the iframe URL
     * `?prefill=...`). Persists for the session. botCore appends this to the
     * system prompt on every agent turn so the bot has the host context from
     * turn 0 — no user typing required.
     */
    setHostContext(value: string | null) {
      this.hostContext = value && value !== '' ? value : null
    },

    pushUserMessage(text: string, files?: ChatFile[]) {
      this.messages.push({
        id: `m_${Date.now()}_u`,
        role: 'user',
        text,
        ts: new Date().toISOString(),
        ...(files && files.length > 0 ? { files } : {}),
      })
    },

    pushAssistantMessage(text: string, actions?: ChatAction[]) {
      this.messages.push({
        id: `m_${Date.now()}_a`,
        role: 'assistant',
        text,
        ts: new Date().toISOString(),
        actions,
      })
    },

    pushAgentMessage(text: string, actions?: ChatAction[], live = false, files?: ChatFile[]) {
      this.messages.push({
        id: `m_${Date.now()}_ag`,
        role: 'agent',
        text,
        ts: new Date().toISOString(),
        actions,
        ...(files && files.length > 0 ? { files } : {}),
        live,
      })
    },

    /**
     * Register the cancel callback of the currently pending auto-redirect
     * countdown (started by the message surface for a live `new_order` message).
     * A new registration replaces the previous one — only one countdown at a time.
     */
    registerPendingRedirect(cancel: () => void) {
      this.cancelPendingRedirect()
      this.pendingRedirectCancel = cancel
    },

    /** Cancel any pending auto-redirect countdown (user started typing or left the view). */
    cancelPendingRedirect() {
      if (this.pendingRedirectCancel) {
        this.pendingRedirectCancel()
        this.pendingRedirectCancel = null
      }
    },

    /** Load initial message history from Core. Last PAGE_SIZE messages, newest last. */
    async loadHistory() {
      if (this.loadedHistory || this.loadingHistory) return
      this.loadingHistory = true

      try {
        const result = await call<MessagesListRequest, MessagesListResponse>(
          CMD.messagesList,
          { limit: PAGE_SIZE },
        )

        if (result.success && result.data && result.data.messages.length > 0) {
          // Map API messages → local format (newest last for display)
          const threadKinds = new Map<number, 'agent' | 'static'>()
          for (const t of result.data.threads) threadKinds.set(t.id, t.kind)
          this.currentThreadId = result.data.currentThreadId

          const mapped: ChatMessage[] = result.data.messages
            .reverse() // API returns newest-first; we want chronological
            .map((m) => mapMessage(m, threadKinds))

          const withDividers = withThreadDividers(mapped, threadKinds)

          // If we got a full page, there might be more
          this.hasMore = result.data.messages.length >= PAGE_SIZE

          // Prepend greeting if messages don't start with one
          if (withDividers[0].role !== 'assistant' && withDividers[0].role !== 'agent') {
            this.messages = [greetingMessage(), ...withDividers]
          } else {
            this.messages = withDividers
          }
        } else {
          // No history — show greeting
          this.messages = [greetingMessage()]
          this.hasMore = false
        }
      } catch {
        // Best-effort: show greeting on failure
        if (this.messages.length === 0) {
          this.messages = [greetingMessage()]
        }
        this.hasMore = false
      }

      this.loadingHistory = false
      this.loadedHistory = true
      this.startPolling()
    },

    /**
     * Load older messages — called when user scrolls to top of transcript.
     * Uses cursor-based pagination via the `before` parameter.
     * Returns the number of messages loaded (0 = no more to load).
     */
    async loadMoreHistory(): Promise<number> {
      if (this.loadingMore || !this.hasMore) return 0
      if (this.messages.length === 0) return 0

      this.loadingMore = true

      // Use the timestamp of the first message as the cursor
      // (exclude the greeting if it's artificial)
      const firstMsg = this.messages[0]
      const before = firstMsg.id.startsWith('m_greeting') && this.messages.length > 1
        ? this.messages[1].ts
        : firstMsg.ts

      try {
        const result = await call<MessagesListRequest, MessagesListResponse>(
          CMD.messagesList,
          { limit: PAGE_SIZE, before },
        )

        if (result.success && result.data && result.data.messages.length > 0) {
          // Thread metadata for the WHOLE transcript (server always returns all
          // threads) — needed to annotate the older page with thread kinds
          // before merging it into the transcript.
          const threadKinds = new Map<number, 'agent' | 'static'>()
          for (const t of result.data.threads) threadKinds.set(t.id, t.kind)

          // Map to local format and reverse (API returns newest-first)
          const older: ChatMessage[] = result.data.messages
            .reverse()
            .map((m) => mapMessage(m, threadKinds))

          this.hasMore = result.data.messages.length >= PAGE_SIZE

          // Rebuild the transcript with thread dividers across the pagination
          // seam: strip existing dividers, merge chronologically, re-insert.
          const realCurrent = this.messages.filter((m) => m.role !== 'divider')
          const merged = withThreadDividers([...older, ...realCurrent], threadKinds)

          // But if first message is an artificial greeting, keep it first
          if (this.messages[0].id === 'm_greeting') {
            const greeting = this.messages.shift()!
            this.messages = [greeting, ...merged]
          } else {
            this.messages = merged
          }

          this.loadingMore = false
          return older.length
        } else {
          this.hasMore = false
        }
      } catch {
        // Best-effort — don't break the UI
         this.hasMore = false
      }

      this.loadingMore = false
      return 0
    },

    /** Send a message to the chat agent via cmd.core.agent.message. */
    async send(wireText?: string, files?: ChatFile[]) {
      const text = this.draft.trim()
      if (!text || this.sending) return
      // `wireText` lets callers send extra context (e.g. attachment contents)
      // to the agent while the bubble keeps only what the user typed.
      const payloadText = (wireText ?? text).trim() || text
      this.sending = true
      // Bump the run epoch: invalidates any in-flight agent-status poll from a
      // previous run so its result can never leak into this run.
      this.runEpoch++
      // Defensive reset: a new run starts with a clean tool sequence and NO
      // status line — a stale status must never show during this run's
      // initial gap (before the first status poll resolves).
      this.agentStatus = null
      this.activity = null
      this.toolDetails = []
      this.pushUserMessage(text, files)
      this.draft = ''
      this.startStatusPolling()

      try {
        const result = await call<AgentMessageRequest, AgentMessageResponse>(
          CMD.agentMessage,
          {
            text: payloadText,
            threadId: this.currentThreadId,
            // Append host-page prefill (e.g. WP product info) to the system
            // prompt on the botCore side. Persisted for the session so the
            // agent ALWAYS has the host context from turn 0.
            ...(this.hostContext ? { hostContext: this.hostContext } : {}),
            ...(files && files.length > 0 ? { files } : {}),
          },
          300000,
        )

        if (result.success && result.data) {
          this.pushAgentMessage(result.data.text, result.data.actions, true, result.data.files)
        } else if (!result.success && result.error === "cancelled") {
          // The user pressed «Отменить» — cancelRun() already reset the UI.
          // Push NOTHING: no agent message, no fallback error text. The
          // trailing stopStatusPolling(); sending = false stays idempotent.
        } else {
          // Agent returned an error — show a soft message, inbox will deliver real response later
          const errMsg = !result.success ? result.error : "unknown"
          const errText = errMsg === "timeout"
            ? "Поиск занимает больше времени, чем ожидалось. Результаты появятся здесь, когда будут готовы."
            : FALLBACK_NO_AGENT_TEXT
          this.pushAgentMessage(errText, FALLBACK_NO_AGENT_ACTIONS)
          // Observability: the user was shown an error text — surface it in ChatLayer.
          trackUserFacingError(errText, { step: "agent-send", error: errMsg })
        }
      } catch {
        // Network error — inbox will deliver when agent finishes
        this.pushAgentMessage(
          "Ищу информацию... Результаты появятся здесь через несколько секунд.",
          FALLBACK_NO_AGENT_ACTIONS,
        )
        trackUserFacingError("Ищу информацию... Результаты появятся здесь через несколько секунд.", {
          step: "agent-send",
          error: "network_error",
        })
      }

      this.stopStatusPolling()
      this.sending = false
    },

    /**
     * Cancel the in-flight agent run. Resets the chat UI instantly (status
     * line + tool lines disappear, run epoch bumped so stale status polls
     * are dropped, button returns to «Отправить»), then fires
     * cmd.core.agent.cancel so botCore aborts the LLM loop for this user.
     * Best-effort: a failed/ignored cancel is benign — the run finishes and
     * the real reply arrives normally.
     */
    async cancelRun() {
      if (!this.sending) return
      // Instant UI reset — the server may take a moment to abort.
      this.stopStatusPolling()
      this.sending = false
      try {
        await call<Record<string, never>, AgentCancelResponse>(CMD.agentCancel, {})
      } catch {
        // Best-effort — ignore cancel failures (no active run, network, etc.)
      }
    },

    /**
     * Start a fresh agent thread: cmd.core.chat.new creates it server-side,
     * then a local "— Новый чат —" divider + greeting open the new transcript
     * segment. Old messages stay in the transcript (scroll-up preserved).
     */
    async newChat() {
      if (this.sending) return
      try {
        const result = await call<Record<string, never>, ChatNewResponse>(CMD.chatNew, {})
        if (result.success && result.data) {
          this.currentThreadId = result.data.threadId
          this.messages.push({
            id: `m_div_newchat_${Date.now()}`,
            role: 'divider',
            text: threadDividerLabel('agent'),
            ts: result.data.createdAt,
            threadId: result.data.threadId,
          })
          this.messages.push(greetingMessage())
          // Path event: user reset the conversation context — a new agent thread.
          trackEvent('chat-new-thread', undefined, {
            threadId: result.data.threadId,
            view: 'chat',
          })
        }
      } catch {
        // Best-effort — a failed chat.new must not break the chat
      }
    },

    /** Poll the webBridge chat inbox for messages pushed via evt.chat.send. */
    async pollInbox() {
      try {
        const res = await fetch(
          `/api/chat-inbox?since=${encodeURIComponent(this.lastPollTimestamp)}`,
          { credentials: "include", headers: authHeaders() },
        );
        if (res.status === 401) return;
        const json = await res.json();
        if (json.success && Array.isArray(json.messages)) {
          for (const m of json.messages as Array<{ id: string; text: string; timestamp: string; meta?: any }>) {
            // Show as in-app toast for completion notifications
            if (m.meta?.action === "search.completed") {
              this.notification = { id: m.id, text: m.text };
            } else {
              this.messages.push({
                id: m.id,
                role: "assistant",
                text: m.text,
                ts: m.timestamp,
              });
            }
            if (m.timestamp > this.lastPollTimestamp) {
              this.lastPollTimestamp = m.timestamp;
            }
          }
        }
      } catch {
        // Best-effort polling — ignore errors
      }
    },

    dismissNotification() {
      this.notification = null;
    },

    startPolling() {
      if (this.pollInterval) return;
      this.pollInterval = setInterval(() => {
        void this.pollInbox();
      }, 3000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
      this.stopStatusPolling();
    },

    /**
     * Poll the webBridge agent-status endpoint for progress events. This is
     * the fallback path: while the SSE stream is connected, polling stays off.
     */
    startStatusPolling() {
      if (this.agentStatusPollId || this.statusStreamLive) return;
      this.agentStatusPollId = setInterval(() => {
        void this.pollAgentStatus();
      }, 1000);
    },

    /** Stop the interval only — the current activity line stays visible. */
    stopStatusPollInterval() {
      // Bump the run epoch: an in-flight poll result is now stale and must
      // not mutate the activity state.
      this.runEpoch++
      if (this.agentStatusPollId) {
        clearInterval(this.agentStatusPollId);
        this.agentStatusPollId = null;
      }
    },

    /**
     * SSE connect/disconnect. A live stream replaces polling (no duplicate
     * events); on error the stream's own reconnect backoff runs while the
     * poll fallback keeps the surface current.
     */
    setStatusStreamLive(live: boolean) {
      this.statusStreamLive = live
      if (live) {
        this.stopStatusPollInterval()
      } else if (this.sending) {
        this.startStatusPolling()
      }
    },

    stopStatusPolling() {
      this.stopStatusPollInterval()
      // Transient run state: the reply has arrived (or the run failed/timed
      // out) — clear the activity line and the tool detail sequence.
      this.agentStatus = null
      this.activity = null
      this.toolDetails = []
    },

    /**
     * Fold one progress event into the run activity. Shared by the polling
     * fallback and the SSE stream so both transports behave identically.
     */
    applyProgress(event: AgentProgressPayload) {
      const now = Date.now()
      if (event.phase === 'tool_running') {
        const label = event.toolLabel ?? event.toolName ?? 'Выполняю действие…'
        const last = this.toolDetails[this.toolDetails.length - 1]
        if (!last || !last.running || last.label !== label) {
          this.toolDetails.push({ label, running: true })
        }
        const stage = event.stage ?? 'other'
        const intent = event.intent ?? label
        if (!this.activity || this.activity.stage !== stage || this.activity.intent !== intent) {
          this.activity = { stage, intent, since: now }
        } else {
          this.activity.failed = false
        }
        this.agentStatus = { phase: event.phase, label: intent }
        return
      }

      if (event.phase === 'tool_completed') {
        const label = event.toolLabel ?? event.toolName
        for (let i = this.toolDetails.length - 1; i >= 0; i--) {
          const detail = this.toolDetails[i]
          if (!detail.running) continue
          if (label && detail.label !== label) continue
          detail.running = false
          if (event.durationMs !== undefined) detail.durationMs = Math.round(event.durationMs)
          detail.failed = event.failed === true
          break
        }
        if (event.failed) {
          this.activity = {
            stage: this.activity?.stage ?? 'other',
            intent: 'Шаг не удался — пробую иначе…',
            since: now,
            failed: true,
          }
          this.agentStatus = { phase: 'thinking', label: this.activity.intent }
        }
        return
      }

      if (event.phase === 'thinking' || event.phase === 'generating') {
        const intent =
          event.intent ?? (event.phase === 'thinking' ? 'Думаю…' : 'Готовлю ответ…')
        if (!this.activity || this.activity.intent !== intent) {
          this.activity = { stage: event.stage ?? 'answer', intent, since: now }
        }
        this.agentStatus = { phase: event.phase, label: intent }
      }
    },

    async pollAgentStatus() {
      // Capture the run epoch BEFORE the fetch. If stopStatusPolling() (or a
      // new send()) bumps it while this fetch is in flight, the result below
      // is stale — it must not mutate any state.
      const epoch = this.runEpoch
      try {
        const res = await fetch("/api/agent-status", { credentials: "include", headers: authHeaders() });
        if (res.status === 401) return;
        const json = await res.json();
        if (epoch !== this.runEpoch) return; // stale — the run already ended
        if (json.success && Array.isArray(json.events) && json.events.length > 0) {
          const events = json.events as AgentProgressPayload[];
          // Each event is delivered exactly once (the queue is cleared per
          // GET), so replaying the whole batch is safe. applyProgress keeps
          // the one-line activity + the detail sequence identical to the SSE
          // path.
          for (const ev of events) {
            if (epoch !== this.runEpoch) return; // stale — the run already ended
            this.applyProgress(ev);
          }
        }
        // If events array is empty, keep the existing status
      } catch {
        // Best-effort — ignore polling errors
      }
    },
  },
})

// --- App closed / hidden beacon -------------------------------------------
// When the tab/app closes or hides, tell webBridge IMMEDIATELY so botCore
// drops the presence record at once instead of waiting out the heartbeat TTL
// (20s). Closing the app right after asking a question must still get the
// agent-reply push to the bot chat. sendBeacon survives page unload.
let presenceOffRegistered = false

function firePresenceOffBeacon(): void {
  try {
    // sendBeacon cannot carry the bearer header — embedded clients (no cookies)
    // must use the keepalive fetch instead, otherwise the beacon 401s silently
    // and the presence record waits out its TTL.
    const hasBearer = getSessionToken() !== null
    if (!hasBearer && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        '/api/chat-presence/off',
        new Blob(['{}'], { type: 'application/json' }),
      )
      if (ok) return
    }
    // Fallback: keepalive fetch (works in most unload paths too)
    void fetch('/api/chat-presence/off', {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: '{}',
      keepalive: true,
    }).catch(() => {})
  } catch {
    // Best-effort — never break unload
  }
}

function registerPresenceOffBeacon(): void {
  if (presenceOffRegistered) return
  presenceOffRegistered = true
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  window.addEventListener('pagehide', firePresenceOffBeacon)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') firePresenceOffBeacon()
  })
}

registerPresenceOffBeacon()
